import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown as ChevronDownIcon,
  FileDown as FileDownIcon,
  FileText as FileTextIcon,
} from 'lucide-react';
import { message } from 'antd';
import type { GeneralViewerInstance } from '../../../helpers/api/view_frame/view-frames';
import {
  downloadAllDicomInstances,
  downloadAllFramesAsJpeg,
  downloadAllFramesAsPng,
  downloadReportPdf,
} from './viewerExportUtils';

type LucideProps = { size?: number; className?: string };
const ChevronDown = ChevronDownIcon as React.FC<LucideProps>;
const FileDown = FileDownIcon as React.FC<LucideProps>;
const FileText = FileTextIcon as React.FC<LucideProps>;

/** Live frame / instance actions from General Viewer instance detail (updated as selection changes). */
export type LiveViewerExportHandlers = {
  exportCurrentDicom: () => void | Promise<void>;
  exportFrameJpeg: () => void | Promise<void>;
  exportViewPng: () => void | Promise<void>;
  canExportViewPng: boolean;
  viewPngTitle?: string;
  instanceIsDicom: boolean;
  /** View detail / toolbar: ZIP of every frame in this anatomical view (optional). */
  exportViewFramesJpeg?: () => void | Promise<void>;
  exportViewFramesPng?: () => void | Promise<void>;
  exportViewAllDicom?: () => void | Promise<void>;
  /** When false, disables view-wide frame ZIP actions. */
  canExportViewFramesZip?: boolean;
};

export type ViewerToolClusterProps = {
  studyUID: string;
  variant?: 'light' | 'dark';
  /** DICOM metadata panel toggle (General Viewer) or open callback (Views / exam). */
  dicomActive?: boolean;
  onDicomClick?: () => void;
  dicomDisabled?: boolean;
  dicomLabel?: string;
  /** Hide DICOM button entirely (e.g. report-only strip). */
  hideDicom?: boolean;
  dicomInstances: GeneralViewerInstance[];
  /** When set, show “current frame / view” export section. */
  liveExport?: LiveViewerExportHandlers | null;
  /** Show PDF + encapsulated DICOM report downloads. */
  showReportExports?: boolean;
  /** When false, hides study-wide DICOM + exam ZIP items (view detail keeps live exports only). */
  showStudyExports?: boolean;
  className?: string;
  /**
   * Exam list / strip: no pill chrome — only square icon buttons (DICOM + export menu).
   */
  actionsIconOnly?: boolean;
  /** With actionsIconOnly: borderless toolbar icons with color (exam table / strip). */
  actionsIconGhost?: boolean;
  /** Exam list, General Viewer: study-wide JPEG/PNG ZIP export (notification progress). */
  examExportMenu?: boolean;
};

const btnBase =
  'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-900 disabled:opacity-40 disabled:pointer-events-none';

/** Shared focus / disabled for standalone toolbar pills (DICOM + Export, non–icon-only). */
const toolbarPillInteractive =
  'inline-flex items-center justify-center gap-1.5 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-900 disabled:opacity-40 disabled:pointer-events-none';

export function ViewerToolCluster({
  studyUID,
  variant = 'light',
  dicomActive = false,
  onDicomClick,
  dicomDisabled = false,
  dicomLabel,
  hideDicom = false,
  dicomInstances,
  liveExport,
  showReportExports = true,
  showStudyExports = true,
  className = '',
  actionsIconOnly = false,
  actionsIconGhost = false,
  examExportMenu = false,
}: ViewerToolClusterProps) {
  const { t } = useTranslation('GeneralViewer');
  const [exportOpen, setExportOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuPortalRef = useRef<HTMLDivElement>(null);
  const [menuFixedStyle, setMenuFixedStyle] = useState<{
    top: number;
    left: number;
    maxHeight: number;
    width: number;
  } | null>(null);

  const updateMenuPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuWidth = 280;
    const gutter = 8;
    let left = rect.right - menuWidth;
    left = Math.max(gutter, Math.min(left, window.innerWidth - menuWidth - gutter));
    const top = rect.bottom + 6;
    const maxHeight = Math.max(120, window.innerHeight - top - gutter);
    setMenuFixedStyle({ top, left, maxHeight, width: menuWidth });
  }, []);

  useLayoutEffect(() => {
    if (!exportOpen) {
      setMenuFixedStyle(null);
      return;
    }
    updateMenuPosition();
  }, [exportOpen, updateMenuPosition]);

  useEffect(() => {
    if (!exportOpen) return;
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [exportOpen, updateMenuPosition]);

  useEffect(() => {
    if (!exportOpen) return;
    const close = (e: MouseEvent) => {
      const node = e.target as Node;
      if (wrapRef.current?.contains(node)) return;
      if (menuPortalRef.current?.contains(node)) return;
      setExportOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExportOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [exportOpen]);

  /** Standalone topbar pills (same footprint as General Viewer “Filters”): h-7, rounded-lg, slate fill + border. */
  const toolbarPillBase =
    'h-7 min-h-[28px] px-3 rounded-lg text-xs font-semibold transition-all duration-150';

  const dicomBtn = variant === 'dark'
    ? dicomActive
      ? `${toolbarPillBase} text-white bg-sky-500/90 border border-sky-400/50 shadow-[0_0_12px_rgba(14,165,233,0.35)]`
      : `${toolbarPillBase} text-white/90 bg-white/10 border border-white/15 hover:bg-white/15`
    : dicomActive
      ? `${toolbarPillBase} text-white bg-sky-600 border border-sky-500 shadow-sm hover:bg-sky-700`
      : `${toolbarPillBase} text-slate-600 bg-[#f1f5f9] border border-[#e2e8f0] hover:bg-[#e2e8f0] hover:text-slate-900 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-700`;

  const exportTriggerLightClosed = `${toolbarPillBase} text-slate-600 bg-[#f1f5f9] border border-[#e2e8f0] hover:bg-[#e2e8f0] hover:text-slate-900 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-700`;
  const exportTriggerLightOpen = `${toolbarPillBase} text-slate-700 bg-[#f1f5f9] border border-indigo-300 ring-2 ring-indigo-400/35 dark:border-indigo-500/50 dark:text-slate-100 dark:bg-slate-800`;

  const exportTriggerDarkClosed = `${toolbarPillBase} text-white/95 bg-white/10 border border-white/15 hover:bg-white/16`;
  const exportTriggerDarkOpen = `${toolbarPillBase} text-white bg-indigo-500/25 border border-indigo-400/40 ring-2 ring-indigo-400/25`;

  const iconOnlyBtn =
    variant === 'dark'
      ? 'h-9 w-9 rounded-lg border border-white/15 bg-white/10 text-white/90 hover:bg-white/15'
      : 'h-9 w-9 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-500';
  const iconOnlyBtnDicomActive =
    variant === 'dark'
      ? 'h-9 w-9 rounded-lg border border-sky-400/50 bg-sky-500/90 text-white shadow-sm'
      : 'h-9 w-9 rounded-lg border border-sky-500 bg-sky-600 text-white shadow-sm';

  const ghostDicom =
    variant === 'dark'
      ? 'h-9 w-9 rounded-lg border-0 bg-transparent text-sky-300 hover:bg-white/10'
      : 'h-9 w-9 rounded-lg border-0 bg-transparent text-sky-600 hover:bg-sky-50 hover:text-sky-700 dark:text-sky-400 dark:hover:bg-sky-950/50 dark:hover:text-sky-300';
  const ghostDicomActive =
    variant === 'dark'
      ? 'h-9 w-9 rounded-lg border-0 bg-sky-500/25 text-sky-200'
      : 'h-9 w-9 rounded-lg border-0 bg-sky-100/90 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300';
  const ghostExport =
    variant === 'dark'
      ? 'h-9 w-9 rounded-lg border-0 bg-transparent text-indigo-300 hover:bg-white/10'
      : 'h-9 w-9 rounded-lg border-0 bg-transparent text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-300';
  const ghostExportOpen =
    variant === 'dark'
      ? 'h-9 w-9 rounded-lg border-0 bg-indigo-500/20 text-indigo-100'
      : 'h-9 w-9 rounded-lg border-0 bg-indigo-50 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200';

  const menuShell =
    variant === 'dark'
      ? 'border border-white/12 bg-zinc-950/95 backdrop-blur-xl shadow-2xl'
      : 'border border-slate-200/90 dark:border-slate-600/80 bg-white dark:bg-slate-900 shadow-xl';

  const itemCls =
    variant === 'dark'
      ? 'w-full text-left px-3 py-2 text-[12px] text-white/90 hover:bg-white/10 rounded-lg mx-1'
      : 'w-full text-left px-3 py-2 text-[12px] text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg mx-1';

  const sectionCls =
    variant === 'dark'
      ? 'px-3 pt-2 pb-1 text-[9px] font-bold uppercase tracking-[0.12em] text-white/40'
      : 'px-3 pt-2 pb-1 text-[9px] font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500';

  const dividerCls = variant === 'dark' ? 'border-white/10' : 'border-slate-200 dark:border-slate-700';

  const runReport = useCallback(
    async (fn: () => Promise<void>) => {
      try {
        await fn();
        setExportOpen(false);
      } catch {
        message.error(t('export.reportDownloadFailed', { defaultValue: 'Download failed.' }));
      }
    },
    [t]
  );

  const showExportDropdown = !!liveExport || showStudyExports || showReportExports;

  const totalStudyInstances = dicomInstances.length;
  const dicomCount = dicomInstances.filter((i) => (i.format_file ?? 'DICOM') === 'DICOM').length;
  const allDicomMenuLabel =
    totalStudyInstances === 0
      ? t('export.allDicomInStudy', { count: dicomCount })
      : dicomCount === totalStudyInstances
        ? t('export.allDicomInStudy', { count: dicomCount })
        : t('export.allDicomInStudyBreakdown', { dicom: dicomCount, total: totalStudyInstances });
  const allDicomDisabledHint =
    dicomCount === 0 && totalStudyInstances > 0 ? t('export.allDicomDisabledNonDicom') : undefined;
  const instancesWithFrames = dicomInstances.some((i) => (i.frame_count ?? 0) > 0);

  const dicomBlock = !hideDicom && onDicomClick && (
    <button
      type="button"
      title={dicomLabel ?? t('dicomMetadata.topbarButton')}
      aria-label={dicomLabel ?? t('dicomMetadata.topbarButton')}
      aria-pressed={dicomActive}
      disabled={dicomDisabled}
      onClick={onDicomClick}
      className={
        actionsIconOnly
          ? `${btnBase} ${
              actionsIconGhost
                ? dicomActive
                  ? ghostDicomActive
                  : ghostDicom
                : dicomActive
                  ? iconOnlyBtnDicomActive
                  : iconOnlyBtn
            }${actionsIconGhost && dicomDisabled ? ' opacity-35' : ''}`
          : `${toolbarPillInteractive} ${dicomBtn}`
      }
    >
      <FileText size={actionsIconOnly ? 17 : 14} strokeWidth={2} />
      {!actionsIconOnly && (
        <span className="hidden sm:inline max-w-[100px] truncate">{dicomLabel ?? t('toolbar.dicomShort')}</span>
      )}
    </button>
  );

  const exportMenuBody = (
    <>
      {liveExport && (
        <>
          <div className={sectionCls}>{t('export.sectionCurrent')}</div>
          <button
            type="button"
            className={itemCls}
            disabled={!liveExport.instanceIsDicom}
            title={!liveExport.instanceIsDicom ? t('export.notDicom') : undefined}
            onClick={() => {
              void Promise.resolve(liveExport.exportCurrentDicom());
              setExportOpen(false);
            }}
          >
            {t('export.currentDicom')}
          </button>
          <button
            type="button"
            className={itemCls}
            onClick={() => {
              void Promise.resolve(liveExport.exportFrameJpeg());
              setExportOpen(false);
            }}
          >
            {t('export.currentFrameJpeg')}
          </button>
          <button
            type="button"
            className={itemCls}
            disabled={!liveExport.canExportViewPng}
            title={liveExport.viewPngTitle}
            onClick={() => {
              void Promise.resolve(liveExport.exportViewPng());
              setExportOpen(false);
            }}
          >
            {t('export.currentViewPng')}
          </button>
          {(liveExport.exportViewFramesJpeg ||
            liveExport.exportViewFramesPng ||
            liveExport.exportViewAllDicom) && (
            <>
              <div className={sectionCls}>{t('export.sectionThisView')}</div>
              {liveExport.exportViewFramesJpeg && (
                <button
                  type="button"
                  className={itemCls}
                  disabled={liveExport.canExportViewFramesZip === false}
                  onClick={() => {
                    void Promise.resolve(liveExport.exportViewFramesJpeg?.());
                    setExportOpen(false);
                  }}
                >
                  {t('export.viewFramesJpegZip')}
                </button>
              )}
              {liveExport.exportViewFramesPng && (
                <button
                  type="button"
                  className={itemCls}
                  disabled={liveExport.canExportViewFramesZip === false}
                  onClick={() => {
                    void Promise.resolve(liveExport.exportViewFramesPng?.());
                    setExportOpen(false);
                  }}
                >
                  {t('export.viewFramesPngZip')}
                </button>
              )}
              {liveExport.exportViewAllDicom && (
                <button
                  type="button"
                  className={itemCls}
                  onClick={() => {
                    void Promise.resolve(liveExport.exportViewAllDicom?.());
                    setExportOpen(false);
                  }}
                >
                  {t('export.viewAllDicom')}
                </button>
              )}
            </>
          )}
          {(showStudyExports || showReportExports) && (
            <div className={`my-1.5 border-t ${dividerCls}`} />
          )}
        </>
      )}

      {showStudyExports && (
        <>
          <div className={sectionCls}>{t('export.sectionStudy')}</div>
          <button
            type="button"
            className={itemCls}
            disabled={dicomCount === 0}
            title={allDicomDisabledHint}
            onClick={() => {
              void downloadAllDicomInstances(
                studyUID,
                dicomInstances.filter((i) => (i.format_file ?? 'DICOM') === 'DICOM'),
                t
              );
              setExportOpen(false);
            }}
          >
            {allDicomMenuLabel}
          </button>

          {examExportMenu && (
            <>
              <button
                type="button"
                className={itemCls}
                disabled={!instancesWithFrames}
                title={!instancesWithFrames ? t('export.noFramePreviewUrls') : undefined}
                onClick={() => {
                  setExportOpen(false);
                  void downloadAllFramesAsJpeg(studyUID, dicomInstances, t);
                }}
              >
                {t('export.downloadAllFirstFramesJpeg')}
              </button>
              <button
                type="button"
                className={itemCls}
                disabled={!instancesWithFrames}
                title={!instancesWithFrames ? t('export.noFramePreviewUrls') : undefined}
                onClick={() => {
                  setExportOpen(false);
                  void downloadAllFramesAsPng(studyUID, dicomInstances, t);
                }}
              >
                {t('export.downloadAllFirstFramesPng')}
              </button>
            </>
          )}
        </>
      )}

      {showReportExports && (
        <>
          <div className={`my-1.5 border-t ${dividerCls}`} />
          <div className={sectionCls}>{t('export.sectionReport')}</div>
          <button
            type="button"
            className={itemCls}
            onClick={() =>
              void runReport(() =>
                downloadReportPdf(studyUID, true, `report_${studyUID}.pdf`, t)
              )
            }
          >
            {t('export.reportPdfWithImages')}
          </button>
          <button
            type="button"
            className={itemCls}
            onClick={() =>
              void runReport(() =>
                downloadReportPdf(studyUID, false, `report_${studyUID}_text.pdf`, t)
              )
            }
          >
            {t('export.reportPdfNoImages')}
          </button>
        </>
      )}
    </>
  );

  const exportBlock = (
    <div className="relative shrink-0" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setExportOpen((o) => !o)}
        className={
          actionsIconOnly
            ? `${btnBase} ${
                actionsIconGhost
                  ? exportOpen
                    ? ghostExportOpen
                    : ghostExport
                  : `${iconOnlyBtn}${exportOpen ? ' ring-2 ring-indigo-400/50 border-indigo-300 dark:border-indigo-600' : ''}`
              }`
            : `${toolbarPillInteractive} ${
                variant === 'dark'
                  ? exportOpen
                    ? exportTriggerDarkOpen
                    : exportTriggerDarkClosed
                  : exportOpen
                    ? exportTriggerLightOpen
                    : exportTriggerLightClosed
              } whitespace-nowrap`
        }
        aria-expanded={exportOpen}
        aria-label={t('export.menu')}
        title={t('export.menu')}
      >
        <FileDown size={actionsIconOnly ? 17 : 14} strokeWidth={2} className="opacity-90 shrink-0" />
        {!actionsIconOnly && (
          <>
            <span className="font-semibold tracking-tight">{t('toolbar.exportShort')}</span>
            <ChevronDown size={12} className={`opacity-70 transition-transform shrink-0 ${exportOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {exportOpen &&
        menuFixedStyle &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={menuPortalRef}
            className={`min-w-[248px] rounded-xl py-1.5 overflow-y-auto overflow-x-hidden ${menuShell}`}
            style={{
              position: 'fixed',
              top: menuFixedStyle.top,
              left: menuFixedStyle.left,
              width: menuFixedStyle.width,
              maxHeight: menuFixedStyle.maxHeight,
              zIndex: 10050,
            }}
          >
            {exportMenuBody}
          </div>,
          document.body
        )}
    </div>
  );

  if (actionsIconOnly) {
    return (
      <div
        className={`inline-flex items-center shrink-0 ${actionsIconGhost ? 'gap-0.5' : 'gap-1'} ${className}`.trim()}
      >
        {dicomBlock}
        {showExportDropdown ? exportBlock : null}
      </div>
    );
  }

  return (
    <div className={`inline-flex shrink-0 flex-nowrap items-center gap-2 ${className}`.trim()}>
      {dicomBlock}
      {showExportDropdown ? exportBlock : null}
    </div>
  );
}
